import { PlusOutlined } from '@ant-design/icons'
import { Button, Card, Tabs, TabsProps } from 'antd'
import { useState } from 'react'
import { useParams } from 'react-router-dom'

import {
  useCounterpartServiceGetInternalInstitutionEndpoint,
  useInstitutionServiceGetInstitutionEndpoint
} from 'src/api/queries'
import { Permissions } from 'src/api/requests'
import { PageBreadcrumbs } from 'src/components'
import { useUser } from 'src/hooks'
import { useLocales } from 'src/locales'

import {
  AddDepartmentModal,
  Equipment,
  OrganizationalProfile,
  OrganizationalStructure
} from '../components'

enum TabKeys {
  Profile = 'profile',
  Structure = 'structure',
  Equipment = 'equipment'
}

export const OrganizationalDetails = () => {
  const [currentTab, setCurrentTab] = useState(TabKeys.Profile)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const { hasPermission } = useUser()

  const { id } = useParams()

  const isAdmin = hasPermission([
    Permissions.ORGANIZATIONAL_STRUCTURE_MENU_ACCESS_ORGANIZATIONAL_STRUCTURE,
    Permissions.ORGANIZATIONAL_STRUCTURE_ORGANIZATIONAL_STRUCTURE_VIEW_ALL
  ])

  const { data: internalInstitution } =
    useCounterpartServiceGetInternalInstitutionEndpoint(
      {
        id: String(id)
      },
      [id],
      { enabled: !isAdmin }
    )

  const { data } = useInstitutionServiceGetInstitutionEndpoint({
    id: isAdmin ? String(id) : String(internalInstitution?.institutionId)
  })

  const { t } = useLocales()

  const breadcrumbItems = [
    {
      title: t('menu.organizational_structure')
    },
    {
      title: String(data?.name)
    }
  ]

  const tabItems: TabsProps['items'] = [
    {
      key: TabKeys.Profile,
      label: t('organizational_structure.profile'),
      children: <OrganizationalProfile id={data?.id} />
    },
    ...(data?.isPhysical
      ? [
          {
            key: TabKeys.Structure,
            label: t('organizational_structure.structure'),
            children: <OrganizationalStructure id={data?.id} />
          }
        ]
      : []),
    ...(hasPermission(
      Permissions.ORGANIZATIONAL_STRUCTURE_MENU_ACCESS_EQUIPMENTS
    )
      ? [
          {
            key: TabKeys.Equipment,
            label: t('organizational_structure.equipment'),
            children: <Equipment id={data?.id} />
          }
        ]
      : [])
  ]

  return (
    <PageBreadcrumbs
      title={data?.name}
      items={breadcrumbItems}
      extra={
        <Button
          icon={<PlusOutlined />}
          type="primary"
          hidden={currentTab !== TabKeys.Structure}
          onClick={() => setIsModalOpen(true)}
        >
          {t(`organizational_structure.department_tab.add_button`)}
        </Button>
      }
    >
      <Card>
        <Tabs
          items={tabItems}
          activeKey={currentTab}
          onChange={(key) => setCurrentTab(key as TabKeys)}
        />
      </Card>
      {isModalOpen && (
        <AddDepartmentModal onCancel={() => setIsModalOpen(false)} />
      )}
    </PageBreadcrumbs>
  )
}
